'use client'

import { ButtonContentLink } from '@fukku/button/ContentLink/ButtonContentLink'
import { BottomSheet } from '@fukku/dialog/BottomSheet/BottomSheet'
import { Content } from '@fukku/dialog/Content/Content'
import type { DialogAPI } from '@fukku/dialog/Dialog'
import { Header } from '@fukku/dialog/Header/Header'
import { Steps, type StepsApi } from '@fukku/dialog/Steps/Steps'
import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import type { BackInStockProps } from 'back-in-stock/src/types'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { ModelInfo } from 'model-info/components/ModelInfo'
import type { Color, Look } from 'model-info/types/Color'
import dynamic from 'next/dynamic'
import { sendShowSizeSelectorEvent } from 'product/analytics/sendShowSizeSelectorEvent'
import { getClientProductStock } from 'product/services/productStock/client/getClientProductStock'
import { GenericEvents } from 'product/types/GenericEvents'
import type { Product } from 'product/types/Product'
import type { ProductLocations } from 'product/types/ProductLocations'
import type { Stock } from 'product/types/Stock'
import { DisclaimerMessage } from 'product-components/components/SizeSelector/DisclaimerMessage/DisclaimerMessage'
import { useBackInStock } from 'product-components/components/SizeSelector/hooks/useBackInStock/useBackInStock'
import { useMessageDisclaimer } from 'product-components/components/SizeSelector/hooks/useMessageDisclaimer/useMessageDisclaimer'
import { useSelectedSize } from 'product-components/components/SizeSelector/hooks/useSelectedSize/useSelectedSize'
import {
	type SelectedSize,
	SizesList,
} from 'product-components/components/SizeSelector/SizesList/SizesList'
import {
	Fragment,
	forwardRef,
	type Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import { useSizeGuide } from 'size-guide/hooks/useSizeGuide/useSizeGuide'
import { useProductSelectedColorSizes } from '../hooks/useProductSelectedColorSizes/useProductSelectedColorSizes'

import styles from './SizesBottomSheet.module.css'

const BackInStockForm = dynamic(
	() =>
		import('../BackInStockForm/BackInStockForm').then(
			(mod) => mod.BackInStockForm
		),
	{ ssr: false }
)

const DynamicSizeGuide = dynamic(
	() =>
		import(
			'product-components/components/SizeSelector/SizeGuide/SizeGuide'
		).then((component) => component.SizeGuide),
	{ ssr: false }
)

export interface SheetProps {
	/** Product info */
	product: Product
	/** Product reference id (8 digits) */
	productId: string
	/** Selected color id (2 characters)*/
	selectedColorId: string
	/** Test id */
	'data-testid'?: string
	/** id */
	id?: string
	/** Callback when bottom sheet is closed */
	onClose?: () => void
	/** Callback when available size is selected */
	onSelectedSize?: (selectedSize: SelectedSize) => void
	/** To show model height and size and check if Size Guide must be shown */
	selectedColor: Color | undefined
	/** To show model height and size. Currently only on PDP */
	selectedLook?: Look
}

export interface SheetExtendedProps extends SheetProps {
	/** Pre computed product color-sizes stock */
	productStock: Stock
	/** Message to show on sizes with no local warehouse */
	delayedMessage?: string
	/** Check if size must be considered as delayed delivery */
	delayedWareHouse?: (warehousesIds?: string[]) => boolean
	/** Where product will be added to bag */
	location: ProductLocations
}

enum NextStep {
	BackInStock = 0,
	SizeGuide = 1,
}

export function SizesBottomSheetRef(
	{
		product,
		productId,
		selectedColorId,
		'data-testid': dataTestId,
		id,
		onClose,
		onSelectedSize,
		selectedColor,
		selectedLook,
		...extended
	}: Readonly<SheetProps | SheetExtendedProps>,
	ref: Ref<DialogAPI | null>
) {
	const { delayedMessage, delayedWareHouse, productStock, location } =
		extended as SheetExtendedProps

	const { t } = useLabels()
	const {
		country: { countryISO },
	} = useMasterData()

	const stepsRef = useRef<StepsApi<BackInStockProps | null>>(null)
	const bottomSheetRef = useRef<DialogAPI>(null)

	const { shouldShowSizeGuide } = useSizeGuide({
		productFamilies: product.families,
		productColorDisclaimer: selectedColor?.disclaimer,
		isProductColorMultisize:
			!!selectedColor?.sizes?.length && selectedColor?.sizes?.length > 1,
	})

	const [nextStep, setNextStep] = useState<NextStep | null>(null)
	const [stock, setStock] = useState<Stock | undefined>(productStock)

	const { disclaimerType } = useMessageDisclaimer({ product })

	const { backInStockModalProps } = useBackInStock()

	useImperativeHandle(ref, () => bottomSheetRef.current as DialogAPI, [])

	const { getSizes, selectedProductId } = useProductSelectedColorSizes({
		product,
		productId,
		selectedColorId,
		delayedWareHouse,
	})

	const handleOnSizeSheetOpen = useCallback(async () => {
		if (!stock) {
			const newStock = await getClientProductStock({
				productId,
				country: countryISO,
			})
			sendShowSizeSelectorEvent({
				colorId: selectedColorId,
				lastUnits: getSizes(newStock ?? undefined).some(
					(size) => size.lastUnits
				),
				productId,
				location,
			})
			setStock(newStock ?? undefined)
		} else {
			sendShowSizeSelectorEvent({
				colorId: selectedColorId,
				lastUnits: getSizes(stock).some((size) => size.lastUnits),
				productId,
				location,
			})
		}
	}, [location, productId, selectedColorId, getSizes, countryISO, stock])

	const selectedSizeHandler = useSelectedSize(
		onSelectedSize,
		productId,
		selectedColorId,
		product,
		bottomSheetRef
	)

	useEffect(() => {
		if (backInStockModalProps && stepsRef.current?.ctx) {
			setNextStep(NextStep.BackInStock)
			stepsRef.current.ctx.detail = backInStockModalProps
			stepsRef.current.next()
		}
	}, [backInStockModalProps])

	const handleOnSizeGuideOpen = useCallback(() => {
		sendGenericEvent(GenericEvents.CLICK_SIZE_GUIDE, {
			colorId: selectedColorId,
			lastUnits: getSizes(stock).some((size) => size.lastUnits),
			productId,
			location,
		})
		setNextStep(NextStep.SizeGuide)
		stepsRef.current?.next()
	}, [location, productId, selectedColorId, getSizes, stock])

	return (
		<BottomSheet
			data-testid={dataTestId}
			id={id}
			title={t('productCard.selectSize.title')}
			ref={bottomSheetRef}
			onClose={onClose}
			onOpen={handleOnSizeSheetOpen}
		>
			<Steps data-testid='product.sizesBottomSheet.content' ref={stepsRef}>
				{(_, prev) => [
					<Fragment key='step1'>
						<Header
							className={styles.header}
							title={t('productCard.selectSize.title')}
							alternativeAction={
								shouldShowSizeGuide ? (
									<ButtonContentLink
										onClick={handleOnSizeGuideOpen}
										data-testid='product.sizesBottomSheet.sizeGuide'
									>
										{t('sizeGuide.link')}
									</ButtonContentLink>
								) : undefined
							}
						/>
						<Content hasPadding={false}>
							{disclaimerType && <DisclaimerMessage type={disclaimerType} />}
							<ModelInfo
								selectedColor={selectedColor}
								selectedLook={selectedLook}
								genderId={product.genderId}
							/>
							<SizesList
								productStock={productStock}
								location={location}
								sizes={getSizes(stock)}
								selectedProductId={selectedProductId}
								selectedColorId={selectedColorId}
								onSelectedSize={selectedSizeHandler}
								delayedMessage={delayedMessage}
								delayedWareHouse={delayedWareHouse}
							/>
						</Content>
					</Fragment>,
					<Fragment key='step2'>
						{nextStep === NextStep.BackInStock && (
							<BackInStockForm prev={prev} />
						)}
						{nextStep === NextStep.SizeGuide && (
							<DynamicSizeGuide
								families={product.families}
								genderId={product.genderId}
								onBack={prev}
							/>
						)}
					</Fragment>,
				]}
			</Steps>
		</BottomSheet>
	)
}

export const SizesBottomSheet = forwardRef(SizesBottomSheetRef)
